import { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Switch, useLocation, useRouteMatch } from 'react-router-dom';

import clean from 'lodash-clean';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';

import { Box } from '@mui/material';

import AnnouncementsListContainer from '../Announcements/AnnouncementsListContainer';
import SplitterContainer from '../Splitter/SplitterContainer';
import MessagesHeaderContainer from './containers/MessagesHeaderContainer';
import MessagesListContainer from './containers/MessagesListContainer';
import MessagesWidgetContainer from './containers/MessagesWidgetContainer';

import { getChats } from './actions';

import { isAdmin } from '../../utils';

import * as commonSelectors from '../Shared/selectors';

import Route from '../../routes/SentryRoute';
import ErrorBoundary from '../Main/ErrorBoundary';
import SecondLineTabs from '../Tabs/SecondLineTabs';

const getInitialSelectValue = () => ({
  value: 'all_properties',
  label: 'All properties',
});

const Messages = (props) => {
  const { match, getThreads, isMobileScreen, newChatData } = props;

  const [selectValue, setSelectValue] = useState(getInitialSelectValue());
  const [search, setSearch] = useState(null);

  const getThreadsList = (queryParams, withReset) => {
    const pmcId = match?.params?.PMCId;
    const buildingId = Number.isInteger(+selectValue.value)
      ? selectValue.value
      : match?.params?.buildingId;
    const params = { q: search, ...queryParams };
    if (pmcId) {
      params.pmc = pmcId;
    }
    if (buildingId && !params?.building) {
      params.building = buildingId;
    }
    return getThreads(params, withReset);
  };

  const onSearch = useCallback(
    debounce((search, propertyId) => {
      const buildingId = Number.isInteger(+propertyId)
        ? propertyId
        : match?.params?.buildingId;
      getThreadsList({ q: search, building: buildingId }, true);
    }, 800),
    [],
  );

  useEffect(() => {
    document.title = 'Messages';
  }, []);

  useEffect(() => {
    // do not run search on init
    if (search !== null) {
      onSearch(search, selectValue?.value);
    }
  }, [search]);

  useEffect(() => {
    getThreadsList({}, true);
  }, [selectValue]);

  const setSearchValue = (e) => setSearch(e.target.value);

  const index = match.url.indexOf('/messages');
  const baseUrl = match.url.slice(0, index);

  const hasSelectedChat = Boolean(match?.params?.id) || !isEmpty(newChatData);

  return (
    <Box height="100%">
      {isMobileScreen && hasSelectedChat ? null : (
        <MessagesHeaderContainer
          {...props}
          selectValue={selectValue}
          setSelectValue={setSelectValue}
          search={search}
          setSearchValue={setSearchValue}
          baseUrl={baseUrl}
        />
      )}
      <SplitterContainer
        isMobileScreen={isMobileScreen}
        hasSelectedItem={hasSelectedChat}
        MainComponent={MessagesListContainer}
        mainComponentProps={{
          ...props,
          getThreadsList,
          baseUrl,
          selectValue,
        }}
        DependentComponent={MessagesWidgetContainer}
        dependentComponentProps={{
          ...props,
          getThreadsList,
          baseUrl,
          selectValue,
          setSearch,
          setSelectValue,
        }}
      />
    </Box>
  );
};

Messages.propTypes = {
  match: PropTypes.instanceOf(Object).isRequired,
  newChatData: PropTypes.instanceOf(Object).isRequired,
  getThreads: PropTypes.func.isRequired,
  isMobileScreen: PropTypes.bool.isRequired,
};

const mapStateToProps = (state, ownProps) => {
  const {
    messages: { results: chats, currentChat },
    newChatData,
  } = state;
  const currentChatId = Number(ownProps?.match?.params?.id);
  const isMobileScreen = commonSelectors.getIsMobileScreen(state);
  return {
    chats,
    newChatData,
    currentChat,
    currentChatId,
    isMobileScreen,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  getThreads: (queryParams, withReset) => {
    const { match } = ownProps;
    const pmcId = Number(match?.params?.PMCId);
    const buildingId = Number(match?.params?.buildingId);
    const params = { ...queryParams };
    if (pmcId) {
      params.pmc = pmcId;
    }
    if (buildingId) {
      params.building = buildingId;
    }
    return getChats(dispatch, clean(params), withReset);
  },
});

const MessagesContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(Messages);

const MessagesRouter = () => {
  const { path, url } = useRouteMatch();
  const location = useLocation();

  // TODO - adjust to support id
  const secondLineTabs = [
    {
      label: 'Messages',
      url: `${url}`,
    },
    {
      label: 'Pop-up messages',
      url: `${url}/announcements`,
    },
  ];

  return (
    <ErrorBoundary>
      {isAdmin() && (
        <>
          <SecondLineTabs
            tabData={secondLineTabs}
            currentUrl={location.pathname}
          />
          <Box sx={{ marginBottom: { xs: '1rem', sm: 0 } }} />
        </>
      )}

      <Switch>
        <Route
          path={`${path}/announcements`}
          component={AnnouncementsListContainer}
        />
        <Route path={`${path}/:id`} component={MessagesContainer} />
        <Route path={`${path}/`} component={MessagesContainer} />
      </Switch>
    </ErrorBoundary>
  );
};

export default MessagesRouter;
